// GauntletHub.tsx, Gauntlet lobby browser and creation
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { useSocket } from '../../../contexts/SocketContext';
import styles from './GauntletHub.module.css';

interface GauntletBracket {
  min: number;
  max: number | null;
  maxParty: number;
  rounds: number;
}

interface Participant {
  id: number;
  mercenaryId: number;
  isReady: boolean;
  mercenary: { id: number; name: string; level: number; race: string };
}

interface GauntletRun {
  id: number;
  bracket: string;
  status: string;
  currentWave: number;
  participants: Participant[];
}

const BRACKET_LABELS: Record<string, { label: string; color: string; levelRange: string }> = {
  iron:   { label: 'Iron Gauntlet',   color: '#9e9e9e', levelRange: 'Lv. 11–20' },
  blood:  { label: 'Blood Gauntlet',  color: '#e53935', levelRange: 'Lv. 21–30' },
  legend: { label: 'Legend Gauntlet', color: '#ffd700', levelRange: 'Lv. 31+' },
};

const BRACKETS_AVAILABLE = new Set(['iron']);

const GauntletHub: React.FC = () => {
  const [runs, setRuns] = useState<GauntletRun[]>([]);
  const [brackets, setBrackets] = useState<Record<string, GauntletBracket>>({});
  const [myRun, setMyRun] = useState<GauntletRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const mountedRef = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();
  // Suppress auto-redirect when the user explicitly navigated back from a run
  const suppressRedirectRef = useRef(!!(location.state as any)?.fromGauntlet);
  const { mercenary } = useMercenary();
  const { socket } = useSocket();

  const flash = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    try {
      const [runsRes, bracketsRes, myRunRes] = await Promise.all([
        axios.get('/api/gauntlet/runs'),
        axios.get('/api/gauntlet/brackets'),
        axios.get('/api/gauntlet/my-run'),
      ]);
      if (!mountedRef.current) return;
      setRuns(runsRes.data);
      setBrackets(bracketsRes.data);
      const run = myRunRes.data;
      setMyRun(run);
      // Auto-redirect only when the user hasn't explicitly chosen to browse
      if (mountedRef.current && run && !suppressRedirectRef.current) {
        navigate(`/gauntlet/${run.id}`, { replace: true });
        return;
      }
    } catch (err) {
      flash('Failed to load Gauntlet data.', false);
    }
    setLoading(false);
  }, [navigate]);

  // Reset or set suppress flag whenever the user navigates here, then fetch
  useEffect(() => {
    if (!(location.state as any)?.fromGauntlet) {
      suppressRedirectRef.current = false;
    } else {
      suppressRedirectRef.current = true;
    }
    mountedRef.current = true;
    fetchData();
    return () => { mountedRef.current = false; };
  }, [mercenary?.id, location.key]);

  // Socket: listen for new runs being created / removed / updated
  useEffect(() => {
    if (!socket) return;
    socket.emit('join_gauntlet_hub');
    const handleUpdate = () => fetchData();
    socket.on('gauntlet_run_created', handleUpdate);
    socket.on('gauntlet_run_ended', handleUpdate);
    socket.on('gauntlet_lobby_update', handleUpdate);
    return () => {
      socket.emit('leave_gauntlet_hub');
      socket.off('gauntlet_run_created', handleUpdate);
      socket.off('gauntlet_run_ended', handleUpdate);
      socket.off('gauntlet_lobby_update', handleUpdate);
    };
  }, [socket, fetchData]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const mercId = mercenary?.id;
      const strategy = (mercId ? localStorage.getItem(`gauntlet_strategy_${mercId}`) : null) || 'Normal';
      const surrenderHP = parseFloat((mercId ? localStorage.getItem(`gauntlet_surrenderHP_${mercId}`) : null) || '10') / 100;
      const res = await axios.post('/api/gauntlet/create', { strategy, surrenderHP });
      navigate(`/gauntlet/${res.data.id}`);
    } catch (err: any) {
      flash(err.response?.data?.message || 'Failed to create run.', false);
    }
    setCreating(false);
  };

  const handleJoin = async (runId: number) => {
    try {
      const mercId = mercenary?.id;
      const strategy = (mercId ? localStorage.getItem(`gauntlet_strategy_${mercId}`) : null) || 'Normal';
      const surrenderHP = parseFloat((mercId ? localStorage.getItem(`gauntlet_surrenderHP_${mercId}`) : null) || '10') / 100;
      await axios.post(`/api/gauntlet/runs/${runId}/join`, { strategy, surrenderHP });
      navigate(`/gauntlet/${runId}`);
    } catch (err: any) {
      flash(err.response?.data?.message || 'Failed to join run.', false);
    }
  };

  const handleAbandon = async (runId: number) => {
    if (!window.confirm('Are you sure you want to abandon this Gauntlet run? You will be dropped out and cannot rejoin.')) return;
    try {
      await axios.post(`/api/gauntlet/runs/${runId}/abandon`);
      setMyRun(null);
      flash('You have abandoned the Gauntlet run.', true);
      fetchData();
    } catch (err: any) {
      flash(err.response?.data?.message || 'Failed to abandon run.', false);
    }
  };

  const bracketMeta = mercenary
    ? (mercenary.level < 11 ? null : mercenary.level <= 20 ? 'iron' : mercenary.level <= 30 ? 'blood' : 'legend')
    : null;

  if (loading) return <div className={styles.loading}>Loading Gauntlet…</div>;

  return (
    <div className={styles.container}>
      {toast && <div className={`${styles.toast} ${toast.ok ? styles.toastOk : styles.toastErr}`}>{toast.msg}</div>}

      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>The Gauntlet</h1>
          <p className={styles.subtitle}>Assemble a party and face 5 waves of increasingly powerful beasts.</p>
        </div>
        <button className={styles.historyBtn} onClick={() => navigate('/gauntlet/history')}>
          ⚔ My History
        </button>
      </div>

      {/* Bracket info cards */}
      <div className={styles.bracketCards}>
        {Object.entries(BRACKET_LABELS).map(([key, meta]) => {
          const cfg = brackets[key];
          const isMyBracket = bracketMeta === key;
          const available = BRACKETS_AVAILABLE.has(key);
          return (
            <div key={key} className={`${styles.bracketCard} ${isMyBracket ? styles.myBracket : ''} ${!available ? styles.bracketLocked : ''}`}
              style={{ borderColor: available ? meta.color : 'rgba(255,255,255,0.1)' }}>
              <h3 style={{ color: available ? meta.color : 'rgba(255,255,255,0.25)' }}>{meta.label}</h3>
              <p>{meta.levelRange}</p>
              {cfg && available && <p>{cfg.rounds} rounds, up to {cfg.maxParty} fighters</p>}
              {isMyBracket && available && <span className={styles.badge}>Your Bracket</span>}
              {!available && <span className={styles.comingSoon}>Coming Soon</span>}
            </div>
          );
        })}
      </div>

      {/* Active run banner, always shown when the user is in an ongoing run */}
      {myRun && (
        <div className={styles.activeRunBanner}>
          <div className={styles.activeRunInfo}>
            <span className={styles.activeRunLabel}>Active Run</span>
            <span className={styles.activeRunDetails}>
              {BRACKET_LABELS[myRun.bracket]?.label ?? myRun.bracket}
              {myRun.status === 'in_progress' ? `, Wave ${myRun.currentWave}` : ', Lobby'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className={styles.rejoinBtn} onClick={() => navigate(`/gauntlet/${myRun.id}`)}>
              Rejoin Run →
            </button>
            {['in_wave', 'voting'].includes(myRun.status) && (
              <button
                className={styles.abandonBtn}
                onClick={() => handleAbandon(myRun.id)}
                title="Abandon this run if you are stuck and cannot continue"
              >
                Abandon
              </button>
            )}
          </div>
        </div>
      )}

      {/* Create button */}
      {mercenary && mercenary.level >= 11 && bracketMeta && BRACKETS_AVAILABLE.has(bracketMeta) && (
        <div className={styles.createRow}>
          <button className={styles.createBtn} onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating…' : '+ Create Run'}
          </button>
        </div>
      )}
      {mercenary && mercenary.level >= 11 && bracketMeta && !BRACKETS_AVAILABLE.has(bracketMeta) && (
        <p className={styles.locked}>The {BRACKET_LABELS[bracketMeta]?.label} is not yet available. Check back soon!</p>
      )}
      {mercenary && mercenary.level < 11 && (
        <p className={styles.locked}>Your mercenary must reach level 11 to enter the Gauntlet.</p>
      )}

      {/* Open runs list */}
      <h2 className={styles.sectionTitle}>Open Runs</h2>
      {runs.filter(r => BRACKETS_AVAILABLE.has(r.bracket)).length === 0 ? (
        <p className={styles.empty}>No open runs. Be the first to create one!</p>
      ) : (
        <div className={styles.runList}>
          {runs.filter(r => BRACKETS_AVAILABLE.has(r.bracket)).map(run => {
            const meta = BRACKET_LABELS[run.bracket];
            const isFull = brackets[run.bracket] && run.participants.length >= brackets[run.bracket].maxParty;
            return (
              <div key={run.id} className={styles.runCard}>
                <div className={styles.runBracket} style={{ color: meta?.color }}>
                  {meta?.label ?? run.bracket}
                </div>
                <div className={styles.runParticipants}>
                  {run.participants.map(p => (
                    <span key={p.id} className={styles.participant}>
                      {p.mercenary.name} <small>Lv.{p.mercenary.level}</small>
                      {p.isReady && <span className={styles.readyDot} title="Ready">●</span>}
                    </span>
                  ))}
                  <span className={styles.partySize}>
                    {run.participants.length}/{brackets[run.bracket]?.maxParty ?? '?'}
                  </span>
                </div>
                <button
                  className={styles.joinBtn}
                  onClick={() => handleJoin(run.id)}
                  disabled={isFull}
                >
                  {isFull ? 'Full' : 'Join'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GauntletHub;
